import React, { useState } from "react";
import { Box, Typography, Card, CardContent, Skeleton } from "@mui/material";

const ChartTooltip = ({ title, stats, position, agentColor }) => (
  <Box
    sx={{
      position: "fixed",
      left: `${position.x - 84}px`,
      top: `${position.y}px`,
      transform: "translateY(-50%)",
      width: "168px",
      height: "112px",
      flexShrink: 0,
      borderRadius: "8px",
      border: "1px solid #D5DCE1",
      background: "#FFF",
      boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
      zIndex: 1000,
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Typography
      sx={{
        color: "#061522",
        fontFamily: "Inter",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "18px",
        marginBottom: "12px",
      }}
    >
      {title}
    </Typography>

    {stats.map((stat, idx) => (
      <Box
        key={idx}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: idx < stats.length - 1 ? "8px" : 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: agentColor || "#148DFD",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                color: "#405261",
                fontFamily: "Inter",
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "18px",
                marginBottom: "2px",
              }}
            >
              {stat.label}
            </Typography>
          </Box>
          <Typography
            sx={{
              color: "#061522",
              fontFamily: "Inter",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "18px",
              marginLeft: "16px",
            }}
          >
            {stat.value}
          </Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const FirstResponseBarChart = ({
  title,
  description,
  data,
  loading = false,
  currentRange = "7d",
  dateRange,
}) => {
  const [tooltip, setTooltip] = useState(null);

  const chartData = data || [];

  const colorPalette = [
    "#148DFD",
    "#FA8C16",
    "#52C41A",
    "#9254DE",
    "#5CDBD3",
    "#FF4D4F",
    "#722ED1",
    "#13C2C2",
    "#52C41A",
    "#FAAD14",
  ];

  const getAgentsFromData = () => {
    if (!chartData || chartData.length === 0) return [];

    const agentMap = new Map();

    chartData.forEach((periodData) => {
      if (periodData.topAgents) {
        periodData.topAgents.forEach((agent) => {
          if (agent.avgResponseTimeSeconds > 0) {
            agentMap.set(agent.agent_id, {
              id: agent.agent_id,
              name: agent.name,
              email: agent.email,
            });
          }
        });
      }
    });

    return Array.from(agentMap.values()).map((agent, index) => ({
      id: agent.id,
      name: agent.name,
      email: agent.email,
      color: colorPalette[index % colorPalette.length],
    }));
  };

  const agents = getAgentsFromData();

  const getPeriodTotalSeconds = (item) => {
    if (!item?.topAgents || item.topAgents.length === 0) return 0;
    return item.topAgents.reduce(
      (sum, a) => sum + (a.avgResponseTimeSeconds || 0),
      0
    );
  };

  const getMaxValue = () => {
    let maxTotal = 0;
    chartData.forEach((d) => {
      const total = getPeriodTotalSeconds(d);
      if (total > maxTotal) maxTotal = total;
    });
    return maxTotal <= 0 ? 60 : maxTotal; // default baseline 60s
  };

  const getAgentResponseTimeSeconds = (dataItem, agentId) => {
    if (dataItem.topAgents) {
      const agent = dataItem.topAgents.find((a) => a.agent_id === agentId);
      return agent?.avgResponseTimeSeconds || 0;
    }
    return 0;
  };

  const getAgentData = (dataItem, agentId) => {
    if (dataItem.topAgents) {
      const agent = dataItem.topAgents.find((a) => a.agent_id === agentId);
      return agent || null;
    }
    return null;
  };

  const hasData = (() => {
    if (!chartData || chartData.length === 0) return false;
    if (!agents || agents.length === 0) return false;
    return chartData.some((item) =>
      agents.some((agent) => getAgentResponseTimeSeconds(item, agent.id) > 0)
    );
  })();

  const leftPad = hasData ? 40 : 16;

  const parseTimeStringToSeconds = (timeString) => {
    if (!timeString || timeString === "0m 0s") return 0;

    let totalSeconds = 0;
    const parts = timeString.split(" ");

    parts.forEach((part) => {
      if (part.includes("h")) {
        totalSeconds += parseInt(part.replace("h", "")) * 3600;
      } else if (part.includes("m")) {
        totalSeconds += parseInt(part.replace("m", "")) * 60;
      } else if (part.includes("s")) {
        totalSeconds += parseInt(part.replace("s", ""));
      }
    });

    return totalSeconds;
  };

  const formatSecondsToTime = (seconds) => {
    if (seconds === 0) return "0s";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return remainingSeconds > 0
        ? `${minutes}m ${remainingSeconds}s`
        : `${minutes}m`;
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  const getNiceScale = (maxValue, numTicks = 5) => {
    if (maxValue <= 0) {
      return { max: 60, ticks: [0, 12, 24, 36, 48, 60] };
    }

    // Calculate nice step size based on max value
    const roughStep = maxValue / numTicks;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const residual = roughStep / magnitude;

    let niceStep;
    if (residual <= 1) niceStep = 1 * magnitude;
    else if (residual <= 2) niceStep = 2 * magnitude;
    else if (residual <= 5) niceStep = 5 * magnitude;
    else niceStep = 10 * magnitude;

    // For time data, ensure minimum step is reasonable
    if (niceStep < 1) niceStep = 1;
    if (maxValue <= 360 && niceStep < 10) niceStep = 10; // finer for seconds up to 6 min

    const niceMax = Math.ceil(maxValue / niceStep) * niceStep;
    const ticks = [];

    for (let i = 0; i <= numTicks; i++) {
      ticks.push(Math.round((i * niceMax) / numTicks));
    }

    return { max: niceMax, ticks };
  };

  const chartHeight = 320; // total area reserved
  const plotHeight = 300; // drawable area so zero baseline is at 300px
  const maxValue = getMaxValue();
  const { max: yMax, ticks: yTicks } = getNiceScale(maxValue, 5);

  // The highest gridline sits a sixth of the way down the plot, so the drawable
  // area represents more than the highest labelled value. Bars are scaled to
  // that taller range, which keeps the tallest one at or below the top gridline
  // instead of running past every label.
  const scaleMax = (yMax * 6) / 5;

  const showSeconds = yMax < 360; // under 6 minutes -> show seconds

  const isCustom = currentRange === "custom";
  const barWidth = 32;
  const customGap = (() => {
    const n = chartData?.length || 0;
    if (n <= 3) return 64;
    if (n <= 6) return 40;
    return 24;
  })();

  // Empty bar style for no-data periods (matches BarChart look & feel)
  const emptyBarStyle = {
    width: "32px",
    height: "4px",
    background: "#F7F8F9",
    borderRadius: "2px",
    flexShrink: 0,
    pointerEvents: "none",
  };

  const handleBarHover = (event, item, index) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const isLast = index === chartData.length - 1;
    const isSecondLast = index === chartData.length - 2;

    setTooltip({
      position: {
        x: isLast || isSecondLast ? rect.left - 90 : rect.left + 120,
        y: rect.top + rect.height / 2,
      },
      data: item,
      index: index,
    });
  };

  const handleBarLeave = () => {
    setTooltip(null);
  };

  const getTooltipData = (item) => {
    const hoveredAgent = agents.find((agent) => agent.id === item.hoveredAgent);
    return {
      title: "Response Time",
      agentColor: hoveredAgent?.color || "#148DFD",
      stats: [
        {
          label: item.hoveredAgentName || "Agent",
          value: item.hoveredValue || "0s",
        },
      ],
    };
  };

  // Fixed 5 gridlines at 1/6..5/6 of the height, top first. With bars scaled to
  // `scaleMax` these land exactly on the nice ticks, so the axis reads
  // 100/80/60/40/20 rather than sixths of an arbitrary range.
  const displayTicks = [...yTicks].slice(1).reverse();
  const formattedYLabels = displayTicks.map((tick) =>
    showSeconds ? `${tick}s` : `${Math.round(tick / 60)}m`
  );

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: "8px",
          p: 0,
          minWidth: "544px",
          width: "100%",
          border: "1px solid #E5E9EC",
        }}
      >
        <CardContent sx={{ p: "24px" }}>
          <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="60%" height={16} sx={{ mb: 3 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "end",
              height: chartHeight,
              gap: 1,
              mb: 2,
            }}
          >
            <Box
              sx={{
                height: chartHeight,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                mr: 1,
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="text" width={40} height={12} />
              ))}
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "end",
                gap: 1,
                height: chartHeight,
              }}
            >
              {Array.from({
                length:
                  currentRange === "30d" ? 15 : currentRange === "1y" ? 12 : 7,
              }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={32}
                    height={Math.random() * 200 + 50}
                    sx={{ borderRadius: "2px", mb: 1 }}
                  />
                  <Skeleton variant="text" width={20} height={12} />
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box
                key={i}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Skeleton variant="circular" width={12} height={12} />
                <Skeleton variant="text" width={80} height={16} />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  const getBarHeight = (item) => {
    const total = getPeriodTotalSeconds(item);
    return (total / scaleMax) * plotHeight;
  };

  const renderChartBars = () => {
    return chartData.map((item, index) => {
      const periodTotal = getPeriodTotalSeconds(item);
      const totalHeightPx = (periodTotal / scaleMax) * plotHeight;
      // Prepare agent segments with positive seconds only, in data order
      const segments = agents
        .map((agent) => ({
          agent,
          seconds: getAgentResponseTimeSeconds(item, agent.id),
          data: getAgentData(item, agent.id),
        }))
        .filter((s) => s.seconds > 0);

      // Compute pixel heights proportionally so sum equals totalHeightPx
      let allocated = 0;
      const heights = segments.map((s, i) => {
        if (i === segments.length - 1) {
          return Math.max(totalHeightPx - allocated, 0);
        }
        const h = (s.seconds / periodTotal) * totalHeightPx;
        const hp = Math.max(Math.floor(h), 0);
        allocated += hp;
        return hp;
      });

      return (
        <Box
          key={index}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
            position: "relative",
            flex: isCustom ? "0 0 auto" : "1",
          }}
        >
          <Box
            sx={{
              width: `${barWidth}px`,
              height: chartHeight,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              position: "relative",
            }}
          >
            {(() => {
              if (segments.length === 0 || totalHeightPx <= 0) {
                return <Box sx={emptyBarStyle} />;
              }
              return segments.map((s, i) => {
                const barHeight = heights[i];
                return (
                  <Box
                    key={s.agent.id}
                    onMouseEnter={(e) => {
                      const enhancedItem = {
                        ...item,
                        hoveredAgent: s.agent.id,
                        hoveredAgentName: s.agent.name,
                        hoveredValue:
                          s.data?.avgResponseTimeFormatted ||
                          formatSecondsToTime(s.seconds),
                        hoveredResponseTime: s.seconds,
                      };
                      handleBarHover(e, enhancedItem, index);
                    }}
                    onMouseLeave={handleBarLeave}
                    sx={{
                      backgroundColor: s.agent.color,
                      height: `${barHeight}px`,
                      width: "32px",
                      borderRadius: "2px",
                      marginBottom: "0px",
                      cursor: "pointer",
                      transition: "opacity 0.2s ease-in-out",
                      "&:hover": {
                        opacity: 0.8,
                      },
                    }}
                  />
                );
              });
            })()}
          </Box>
        </Box>
      );
    });
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "8px",
        border: "1px solid #E5E9EC",
        background: "#FFF",
        width: "100%",
        p: 0,
        minWidth: "100px",
      }}
    >
      <CardContent sx={{ p: '24px' }}>
        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter, sans-serif",
            fontSize: "18px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "18px",
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "#405261",
            fontFamily: "Inter, sans-serif",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "16px",
            mb: 2,
          }}
        >
          {description}
        </Typography>
        <Box sx={{ position: "relative", }}>
          <Box
            sx={{
              position: "absolute",
              left: leftPad,
              right: 0,
              top: 0,
              height: plotHeight,
              zIndex: 0,
              pointerEvents: "none",
            }}
          >
            {Array.from({ length: 5 }, (_, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: `${((i + 1) / 6) * plotHeight}px`,
                  borderTop: "1px dashed #E5E9EC",
                }}
              />
            ))}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: `${plotHeight}px`,
                borderTop: "1px solid #E5E9EC",
              }}
            />
          </Box>
          {hasData && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: leftPad,
                height: plotHeight,
                display: "block",
                alignItems: "flex-start",
                paddingRight: "8px",
                boxSizing: "border-box",
                zIndex: 1,
                overflow: "hidden",
              }}
            >
              {[...displayTicks].map((_, idx) => (
                <Typography
                  key={idx}
                  sx={{
                    position: "absolute",
                    top: `${((idx + 1) / 6) * plotHeight - 24}px`,
                    right: 8,
                    fontSize: "12px",
                    lineHeight: "12px",
                    color: "#405261",
                    fontFamily: "Inter",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formattedYLabels[idx]}
                </Typography>
              ))}
              <Typography
                sx={{
                  position: "absolute",
                  top: `${plotHeight - 24}px`,
                  right: 8,
                  fontSize: "12px",
                  lineHeight: "12px",
                  color: "#405261",
                  fontFamily: "Inter",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                {showSeconds ? "0s" : "0m"}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              marginLeft: { xs: "30px", sm: "40px" },
              paddingRight: isCustom ? { xs: "30px", sm: "40px" } : 0,
              height: plotHeight,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: isCustom ? "center" : "space-between",
              columnGap: isCustom ? `${customGap}px` : undefined,
              position: "relative",
              zIndex: 1,
            }}
          >
            {renderChartBars()}
          </Box>

          <Box
            sx={{
              marginLeft: { xs: "30px", sm: "40px" },
              paddingRight: isCustom ? { xs: "30px", sm: "40px" } : 0,
              display: "flex",
              justifyContent: isCustom ? "center" : "space-between",
              columnGap: isCustom ? `${customGap}px` : undefined,
              marginTop: "8px",
            }}
          >
            {chartData.map((item, index) => (
              <Box
                key={index}
                sx={{
                  flex: isCustom ? "0 0 auto" : "1",
                  width: isCustom ? `${barWidth}px` : "auto",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "10px", sm: "12px" },
                    color: "#405261",
                    fontFamily: "Inter",
                    textAlign: "center",
                  }}
                >
                  {item.day}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",

              marginBottom: '8px',
              gap: { xs: "12px", sm: "24px" },
              marginTop: "24px",
            }}
          >
            {agents.map((agent) => (
              <Box
                key={agent.id}
                sx={{
                  display: "flex", alignItems: "center", gap: "6px",

                }}
              >
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    backgroundColor: agent.color,
                    borderRadius: "50%",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#405261",
                    fontFamily: "Inter",
                  }}
                >
                  {agent.name}
                </Typography>
              </Box>
            ))}
          </Box>

          {tooltip && (
            <ChartTooltip
              {...getTooltipData(tooltip.data)}
              position={tooltip.position}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FirstResponseBarChart;
